import { PartnerIntegrationCard } from 'lib-integrations-partner-widgets';
import type { ShowNotificationFunctionProps } from 'lib-integrations-partner-widgets';
import { useHasAccess } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import {
  MetaConversation,
  NotificationPayloadT,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { ElevionNode } from 'lib-shared-types-api';
import { useCallback, useMemo, useRef } from 'react';

import { notificationActions } from 'components/notifications/actions/notification';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage';

import { ElevioCard } from '../../../integrations/elevio/src/components/ElevioCard';
import { KnowledgeSuggestions } from '../../../integrations/knowledge/components/knowledgeSuggestions';
import ContactCard from '../../cards/ContactCard';
import { ContactMemoriesCard } from '../../cards/contactMemoriesCard';
import { CustomCardsItem } from '../../cards/customCardsItem';
import { ConversationCustomFieldsCard } from '../../cards/customFields';
import { HistoryCard } from '../../cards/historyCard/HistoryCard';
import { TimelineCard } from '../../cards/timelineCard';
import { UserTrackingCard } from '../../cards/userTrackingCard';
import { CardMoveHandle } from '../../components/cardMoveHandle/cardMoveHandle';
import { VisibilityToggle } from '../../components/visibilityToggle/visibilityToggle';
import { SidebarCardKey } from '../../conversationSidebar.types';
import { partnerIntegrationNotificationTimeout } from '../../partnerIntegrations';
import { cardKey } from '../../utils/cardKey';
import { matchesRenderConditions } from '../../utils/conditions';
import { MergeableCard, mergeSidebarCards } from '../mergeSidebarCards';
import {
  SidebarCardCatalogItem,
  useSidebarCardCatalog,
} from '../useSidebarCardCatalog/useSidebarCardCatalog';
import { useSidebarConditionContext } from '../useSidebarConditionContext/useSidebarConditionContext';

/** Rendering context passed to each card's `render` in the flat list. */
export type SidebarCardRenderContext = {
  readonly index: number;
  readonly isMinimized: boolean;
  readonly type: symbol;
  readonly onMove: (args: {
    readonly dragIndex: number;
    readonly hoverIndex: number;
  }) => void;
};

/** A renderable flat sidebar card. */
export type SidebarCardDescriptor = MergeableCard & {
  /** The card's display name, carried through so failures can name the card. */
  readonly label: string;
  readonly render: (ctx: SidebarCardRenderContext) => JSX.Element | null;
};

type KeyedDescriptor = SidebarCardDescriptor & { readonly key: SidebarCardKey };

type UseSidebarCardsArgs = {
  readonly metaConversation: MetaConversation;
  readonly isCardOpen: (key: string) => boolean;
  readonly toggleCard: (key: string) => void;
};

export type UseSidebarCardsResult = {
  /** All cards in effective order (hidden included) — used in customization mode. */
  readonly orderedCards: readonly KeyedDescriptor[];
  /** Only the cards that should render in default mode. */
  readonly visibleCards: readonly KeyedDescriptor[];
  /** Whether the card is hidden for any reason (org, user, or conditions). */
  readonly isHidden: (key: SidebarCardKey) => boolean;
  /** Whether the card is hidden because its conditions didn't match. */
  readonly isConditionHidden: (key: SidebarCardKey) => boolean;
  /**
   * Whether the card is hidden by the org default or the agent's own choice,
   * ignoring conditions — what the visibility toggle reflects.
   */
  readonly isUserHidden: (key: SidebarCardKey) => boolean;
  readonly toggleVisibility: (key: SidebarCardKey) => void;
  readonly reorder: (args: {
    readonly dragIndex: number;
    readonly hoverIndex: number;
  }) => void;
  /** Shared drag-drop type so every card belongs to the same reorderable list. */
  readonly dragType: symbol;
  /** How many cards are hidden for any reason (org, user, or conditions). */
  readonly hiddenCount: number;
  /** How many cards are hidden specifically because their conditions didn't match. */
  readonly conditionHiddenCount: number;
};

/**
 * Builds the effective flat conversation sidebar card list
 * (`sidebar-cards-admin-management`). The shared {@link useSidebarCardCatalog}
 * decides *which* cards exist; this hook attaches each card's `render` closure
 * (reusing the existing card components), applies the gating that only makes
 * sense for a live conversation, then merges the org default configuration with
 * the agent's personal overrides and per-card render conditions to produce the
 * ordered/visible lists plus reorder and visibility controls.
 *
 * Live-conversation gating:
 * - custom-fields / history are dropped when the conversation lacks the data or
 *   the agent lacks access;
 * - knowledge suggestions require read access.
 */
export const useSidebarCards = ({
  metaConversation,
  isCardOpen,
  toggleCard,
}: UseSidebarCardsArgs): UseSidebarCardsResult => {
  const dispatch = useAppDispatch();
  const { cards: catalog } = useSidebarCardCatalog();

  const flags = useFeatureFlag(['contact-activity-log']);
  const hasCustomAttributeAccess = useHasAccess(
    'conversation.custom_attribute',
  );

  const storage = useServerStorage('user');
  const orgStorage = useServerStorage('organization');
  const orgConfig = orgStorage.data.conversation_sidebar;

  const dragTypeRef = useRef(Symbol('flatSidebarCard'));

  const showNotification = useCallback(
    ({ title, text, icon, severity }: ShowNotificationFunctionProps) => {
      const payload: NotificationPayloadT = {
        title,
        text,
        icon,
        className: severity as string,
        time:
          severity === 'warning' || severity === 'danger'
            ? 0
            : partnerIntegrationNotificationTimeout,
      };
      dispatch(notificationActions.notificationAdd(payload));
    },
    [dispatch],
  );

  const cards = useMemo<SidebarCardDescriptor[]>(() => {
    const conversationId = metaConversation.conversation?.id;
    const descriptors: SidebarCardDescriptor[] = [];

    const push = (
      item: SidebarCardCatalogItem,
      render: SidebarCardDescriptor['render'],
    ) =>
      descriptors.push({
        cardType: item.cardType,
        cardId: item.cardId,
        defaultIndex: item.defaultIndex,
        label: item.label,
        render,
      });

    for (const item of catalog) {
      if (item.cardType === 'native' && item.widget) {
        const widget = item.widget;
        push(item, ({ index, isMinimized, type, onMove }) => (
          <PartnerIntegrationCard
            widget={widget}
            index={index}
            configuration={{ baseUrl: config.url.partnerIntegrations }}
            conversationContext={{
              conversationId: conversationId || null,
              agent:
                metaConversation.conversation?.assignment?.agent ?? undefined,
              customer: metaConversation.requester ?? undefined,
            }}
            showNotification={showNotification}
            onToggleFn={toggleCard}
            isOpenFn={isCardOpen}
            isMinimized={isMinimized}
            renderDragElement={(_key, idx, target) => (
              <CardMoveHandle
                index={idx}
                type={type}
                target={target}
                onMove={onMove}
              />
            )}
            renderVisibilityToggleElement={() => <VisibilityToggle />}
          />
        ));
        continue;
      }

      if (item.cardType === 'custom' && item.customCard) {
        const card = item.customCard;
        push(item, ({ index, isMinimized, type, onMove }) => (
          <CustomCardsItem
            card={card}
            isMinimized={isMinimized}
            csid={conversationId}
            index={index}
            type={type}
            onMove={onMove}
          />
        ));
        continue;
      }

      switch (item.cardId) {
        case 'contact':
          push(item, ({ index, isMinimized, type, onMove }) => (
            <ContactCard
              onToggle={() => toggleCard('contact')}
              isOpen={isCardOpen('contact')}
              isMinimized={isMinimized}
              index={index}
              type={type}
              onMove={onMove}
            />
          ));
          break;

        case 'contact-memories':
          push(item, ({ index, isMinimized, type, onMove }) => (
            <ContactMemoriesCard
              isMinimized={isMinimized}
              isOpen={isCardOpen('contact-memories')}
              onToggle={() => toggleCard('contact-memories')}
              index={index}
              type={type}
              onMove={onMove}
            />
          ));
          break;

        case 'custom-fields':
          if (hasCustomAttributeAccess && conversationId) {
            push(item, ({ index, isMinimized, type, onMove }) => (
              <ConversationCustomFieldsCard
                csid={conversationId}
                isMinimized={isMinimized}
                isOpen={isCardOpen('custom-fields')}
                onToggle={() => toggleCard('custom-fields')}
                index={index}
                type={type}
                onMove={onMove}
              />
            ));
          }
          break;

        case 'history':
          if (flags.contactActivityLog) {
            const { requester } = metaConversation;
            if (requester) {
              push(item, ({ index, isMinimized, type, onMove }) => (
                <TimelineCard
                  onToggle={() => toggleCard('history')}
                  isOpen={isCardOpen('history')}
                  requester={requester}
                  isMinimized={isMinimized}
                  index={index}
                  type={type}
                  onMove={onMove}
                />
              ));
            }
          } else if (
            metaConversation.requester &&
            metaConversation.previousConversations &&
            metaConversation.previousConversations.length !== 0
          ) {
            const { requester, previousConversations } = metaConversation;
            push(item, ({ index, isMinimized, type, onMove }) => (
              <HistoryCard
                onToggle={() => toggleCard('history')}
                isOpen={isCardOpen('history')}
                previousConversations={previousConversations}
                requester={requester}
                isMinimized={isMinimized}
                index={index}
                type={type}
                onMove={onMove}
              />
            ));
          }
          break;

        case 'tracking': {
          const attributes = metaConversation.conversation?.attributes ?? {};
          push(item, ({ index, isMinimized, type, onMove }) => (
            <UserTrackingCard
              onToggle={() => toggleCard('tracking')}
              isOpen={isCardOpen('tracking')}
              attributes={attributes}
              isMinimized={isMinimized}
              index={index}
              type={type}
              onMove={onMove}
            />
          ));
          break;
        }

        case 'knowledge-suggestions':
          push(item, ({ index, isMinimized, type, onMove }) => (
            <KnowledgeSuggestions
              isMinimized={isMinimized}
              index={index}
              type={type}
              onMove={onMove}
            />
          ));
          break;

        case 'elevio':
          push(item, ({ index, isMinimized, type, onMove }) => (
            <ElevioCard
              isMinimized={isMinimized}
              index={index}
              type={type}
              onMove={onMove}
            />
          ));
          break;
      }
    }

    return descriptors;
  }, [
    catalog,
    metaConversation,
    flags.contactActivityLog,
    hasCustomAttributeAccess,
    isCardOpen,
    toggleCard,
    showNotification,
  ]);

  // Effective conditions per card, taken from the saved org-config entry.
  const conditionsByKey = useMemo(() => {
    const orgByKey = new Map(
      (orgConfig?.cards ?? []).map((entry) => [cardKey(entry), entry]),
    );
    const map = new Map<SidebarCardKey, readonly ElevionNode[]>();
    cards.forEach((card) => {
      const key = cardKey(card);
      const conditions = orgByKey.get(key)?.conditions ?? [];
      if (conditions.length > 0) {
        map.set(key, conditions);
      }
    });
    return map;
  }, [orgConfig, cards]);

  const anyConditions = conditionsByKey.size > 0;
  const context = useSidebarConditionContext(metaConversation, anyConditions);

  const conditionMatches = useCallback(
    (key: SidebarCardKey) =>
      matchesRenderConditions(conditionsByKey.get(key), context),
    [conditionsByKey, context],
  );

  const merged = useMemo(
    () =>
      mergeSidebarCards({
        cards,
        orgConfig,
        userOrder: storage.data.conversation?.sidebarCardOrder,
        userVisibility: storage.data.conversation?.sidebarVisibilityOverrides,
        conditionMatches,
      }),
    [
      cards,
      orgConfig,
      storage.data.conversation?.sidebarCardOrder,
      storage.data.conversation?.sidebarVisibilityOverrides,
      conditionMatches,
    ],
  );

  // Every card in its configured order, hidden ones included and kept in place
  // (the renderer dims them).
  const orderedCards = merged.orderedCards;

  // Latest displayed key order, read by `reorder` at drop time (drag indices
  // refer to positions in `orderedCards`).
  const orderedKeysRef = useRef<readonly SidebarCardKey[]>([]);
  orderedKeysRef.current = orderedCards.map((card) => card.key);

  const persistOrder = useCallback(
    (keys: readonly SidebarCardKey[]) => {
      storage.set({
        conversation: (draft) => {
          draft.sidebarCardOrder = [...keys];
        },
      });
    },
    [storage],
  );

  const reorder = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      const next = [...orderedKeysRef.current];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, moved);
      persistOrder(next);
      ampli.conversationRightSidebarItemReordered({ sidebarItemType: 'card' });
    },
    [persistOrder],
  );

  const toggleVisibility = useCallback(
    (key: SidebarCardKey) => {
      // Flip the agent's own choice (ignoring conditions), so a condition-hidden
      // card can still be toggled — the override value is "visible?".
      const currentlyUserHidden = merged.visibilityHiddenKeys.has(key);
      storage.set({
        conversation: (draft) => {
          draft.sidebarVisibilityOverrides = {
            ...draft.sidebarVisibilityOverrides,
            [key]: currentlyUserHidden,
          };
        },
      });
    },
    [merged.visibilityHiddenKeys, storage],
  );

  const isHidden = useCallback(
    (key: SidebarCardKey) => merged.hiddenKeys.has(key),
    [merged.hiddenKeys],
  );
  const isConditionHidden = useCallback(
    (key: SidebarCardKey) => merged.conditionHiddenKeys.has(key),
    [merged.conditionHiddenKeys],
  );
  const isUserHidden = useCallback(
    (key: SidebarCardKey) => merged.visibilityHiddenKeys.has(key),
    [merged.visibilityHiddenKeys],
  );

  const visibleCards = useMemo(
    () => orderedCards.filter((card) => !merged.hiddenKeys.has(card.key)),
    [orderedCards, merged.hiddenKeys],
  );

  return {
    orderedCards,
    visibleCards,
    isHidden,
    isConditionHidden,
    isUserHidden,
    toggleVisibility,
    reorder,
    dragType: dragTypeRef.current,
    hiddenCount: merged.hiddenKeys.size,
    conditionHiddenCount: merged.conditionHiddenKeys.size,
  };
};
